import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BoostIcon } from '@openx/components-core/icons/BoostIcon/BoostIcon.tsx';
import { CustomIcon } from '@openx/components-core/icons/CustomIcon/CustomIcon.tsx';
import { FireIcon } from '@openx/components-core/icons/FireIcon/FireIcon.tsx';
import { SparkleIcon } from '@openx/components-core/icons/SparkleIcon/SparkleIcon.tsx';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { RadioComponentSwitchFilled } from '@openx/components-core/lib/RadioComponentSwitchFilled/RadioComponentSwitchFilled.tsx';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { type DealApi, getRboStatus, rboConfigMode, rboStatus } from '@openx/types/deal.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { type ReactNode, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { DealDetailsSectionProps } from '../../../types.ts';
import { GroupDealRboAlert } from '../../components/DealGroup/GroupDealRboAlert.tsx';
import { RBOStatusErrorAlert } from '../../components/ResultsByOpenX/RBOStatusErrorAlert.tsx';
import { ResultsByOpenXReadonly } from '../../components/ResultsByOpenX/ResultsByOpenXReadonly.tsx';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { useShowRboLightweight } from '../../utils/useShowRboLightweight.ts';

export type ResultsByOpenXSectionDeal = Pick<DealApi, 'deal_id' | 'rbo_config'>;

export function ResultsByOpenXSection<T extends ResultsByOpenXSectionDeal>({
  deal,
  historyMode,
}: DealDetailsSectionProps<T>) {
  const { t } = useTranslation();
  const { useDealGroupFetch, useDealRboInfoFetch } = useDealContext();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { deal_id: dealId, rbo_config: rboConfig } = deal;
  const isRboLightweightUser = useShowRboLightweight();

  const { group, loading: groupInfoLoading } = useDealGroupFetch(dealId ?? '', historyMode);

  const { isRboUser, isRboAdmin } = useMemo(
    () => ({
      isRboUser: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_RBO_CONFIG_READ)),
      isRboAdmin: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_RBO_CONFIG_ADMIN_READ)),
    }),
    [isAllowed]
  );
  const { rboInfo, loading: rboInfoLoading } = useDealRboInfoFetch({
    dealId: dealId ?? '',
    skip: historyMode || !isRboAdmin,
  });

  const { groupDealWithRbo, groupHasOtherDealWithRbo } = useMemo(() => {
    const groupDealWithRbo = group.find(({ rboConfig }) => !!rboConfig);
    return {
      groupDealWithRbo,
      groupHasOtherDealWithRbo: !!groupDealWithRbo && !rboConfig,
    };
  }, [group, rboConfig]);

  const rboConfigReadonly = useMemo(() => {
    const rboConfigReadonly = rboConfig ?? groupDealWithRbo?.rboConfig;

    if (!rboConfigReadonly) return;

    return {
      ...rboConfigReadonly,
      threshold: rboConfigReadonly?.threshold ?? rboInfo[0]?.threshold,
    };
  }, [groupDealWithRbo?.rboConfig, rboConfig, rboInfo]);

  const hasRelatedRbo = useMemo(
    () => rboConfigReadonly || groupHasOtherDealWithRbo,
    [groupHasOtherDealWithRbo, rboConfigReadonly]
  );
  const showRboCustomMode = useMemo(
    () => isRboAdmin || rboConfigReadonly?.mode === rboConfigMode.CUSTOM || !rboConfigReadonly?.mode,
    [isRboAdmin, rboConfigReadonly]
  );
  const allowed = useMemo(() => isRboLightweightUser || isRboUser, [isRboLightweightUser, isRboUser]);
  const emptyHistoryEntry = historyMode && !rboConfig;

  const statusMessageMap = useMemo(() => getRboStatus(t), [t]);
  const tooltipMessage = statusMessageMap[rboConfig?.status ?? rboStatus.PENDING];

  const infoAlert = useMemo<ReactNode[]>(() => {
    const alerts: ReactNode[] = [];
    if (groupHasOtherDealWithRbo && groupDealWithRbo) {
      alerts.push(<GroupDealRboAlert deal={groupDealWithRbo} sx={{ marginBottom: 1.5 }} />);
    }
    if (rboConfigReadonly?.status === rboStatus.ERROR) {
      alerts.push(<RBOStatusErrorAlert message={rboConfigReadonly.message} />);
    }
    return alerts;
  }, [groupHasOtherDealWithRbo, groupDealWithRbo, rboConfigReadonly]);

  const performanceQualityFilterRadioOptions = useMemo(
    () => [
      {
        value: rboConfigMode.BOOST,
        dataTest: 'boost-performance-quality-filter-radio',
        helperText: (
          <Trans i18nKey="dealDetailsRboBoostOptionDescription">
            Exclude requests with <strong>the lowest</strong> ad engagement scores.
          </Trans>
        ),
        label: t('Boost'),
        tooltip: null,
        isAllowed: true,
        child: null,
        icon: <BoostIcon height={18} />,
      },
      {
        value: rboConfigMode.MAX,
        dataTest: 'max-performance-quality-filter-radio',
        helperText: (
          <Trans i18nKey="dealDetailsRboMaxOptionDescription">
            Target requests with <strong>the highest</strong> ad engagement scores.
          </Trans>
        ),
        label: t('Max'),
        tooltip: t('Once active, the feature may take up to 48 hours to converge to optimal deal volume & threshold.'),
        isAllowed: true,
        child: null,
        icon: <FireIcon height={18} />,
      },
      {
        value: rboConfigMode.CUSTOM,
        dataTest: 'custom-performance-quality-filter-radio',
        helperText: t('Set custom optimization settings for the feature.'),
        label: t('Custom'),
        tooltip: null,
        isAllowed: showRboCustomMode,
        child: null,
        icon: <CustomIcon height={18} />,
      },
    ],
    [showRboCustomMode, t]
  );

  if (!allowed || !hasRelatedRbo || emptyHistoryEntry) {
    return;
  }

  if (groupInfoLoading || rboInfoLoading) {
    return <Loader />;
  }

  return (
    <FormSection
      data-test="results-by-openx"
      divider
      historyMode={historyMode}
      title={t('Engagement Optimization')}
      titleIcon={<SparkleIcon />}
      description={
        <Tooltip title={tooltipMessage} data-test="rbo-status">
          <Typography component="span">
            <StatusIndicator status={rboConfigReadonly?.status ?? rboStatus.PENDING} />
          </Typography>
        </Tooltip>
      }
    >
      {infoAlert.map((alert, index) => (
        <Box key={index}>{alert}</Box>
      ))}
      {!isRboLightweightUser ? (
        <ResultsByOpenXReadonly rboConfig={rboConfigReadonly} />
      ) : (
        <Box data-test="performance-quality-filter-container">
          <FieldDetails
            toggle
            dataTest="enable-results-by-openx"
            title={t('Enable Engagement Optimization')}
            value={!!rboConfigReadonly}
          />
          <RadioComponentSwitchFilled
            value={rboConfigReadonly?.mode ?? rboConfigMode.CUSTOM}
            radioOptions={performanceQualityFilterRadioOptions}
            disabled
            isDetailsPage
          />
          {isRboAdmin && <ResultsByOpenXReadonly rboConfig={rboConfigReadonly} title={t('Custom Settings')} />}
        </Box>
      )}
    </FormSection>
  );
}
